// src/pages/Shares.tsx
import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  message,
  Input,
  Modal,
  Tooltip
} from 'antd';
import {
  ShareAltOutlined,
  CopyOutlined,
  EyeOutlined,
  StopOutlined,
  MailOutlined,
  LockOutlined,
  CalendarOutlined,
  SearchOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { useCustom, useCustomMutation } from '@refinedev/core';
import { ShareAnalyticsDrawer } from '../components/ShareAnalyticsDrawer';
import { PDFViewerModal } from '../components/pdf-viewer';
import { authenticatedFetch } from '../providers/authProvider';
import type { Share } from '../types/share';

const { Search } = Input;

const Shares: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [analyticsVisible, setAnalyticsVisible] = useState(false);
  const [selectedShare, setSelectedShare] = useState<Share | null>(null);
  const [pdfViewerState, setPdfViewerState] = useState<{
    visible: boolean;
    pdfUrl: string;
    documentId: string;
    documentTitle: string;
  }>({ visible: false, pdfUrl: '', documentId: '', documentTitle: '' });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch user's shares
  const { data, isLoading, refetch } = useCustom<{ shares: Share[] }>({
    url: '/user-shares',
    method: 'get',
  });

  const { mutate: revokeShare } = useCustomMutation();

  // Extract shares - dataProvider wraps backend response in extra 'data' layer
  // Backend returns: { status: 'success', data: { shares: [...] } }
  // dataProvider wraps it: { data: { status: 'success', data: { shares: [...] } } }
  const shares: Share[] = (data as any)?.data?.data?.shares || [];

  const handleCopyLink = (shareUrl: string) => {
    // Backend returns full URL, use it directly
    navigator.clipboard.writeText(shareUrl).then(() => {
      message.success('Share link copied to clipboard!');
    }).catch(() => {
      message.error('Failed to copy link');
    });
  };

  const handleViewAnalytics = (share: Share) => {
    setSelectedShare(share);
    setAnalyticsVisible(true);
  };

  const handleRevokeShare = (shareToken: string, videoTitle: string) => {
    Modal.confirm({
      title: 'Revoke Share Link',
      content: `Are you sure you want to revoke the share link for "${videoTitle}"? This action cannot be undone.`,
      okText: 'Revoke',
      okType: 'danger',
      onOk: () => {
        revokeShare({
          url: `/revoke-share/${shareToken}`,
          method: 'post',
          values: {},
        }, {
          onSuccess: () => {
            message.success('Share link revoked successfully');
            refetch();
          },
          onError: (error: any) => {
            console.error('Error revoking share:', error);
            message.error('Failed to revoke share link');
          },
        });
      },
    });
  };

  const handleViewComments = async (share: Share) => {
    try {
      // Fetch PDF as blob through authenticated proxy to avoid CORS issues
      const response = await authenticatedFetch(`/proxy-pdf/${share.jobId}`);

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to load PDF' }));
        throw new Error(error.error || 'Failed to load PDF');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      setPdfViewerState({
        visible: true,
        pdfUrl: blobUrl,
        documentId: share.jobId,
        documentTitle: share.videoTitle,
      });
    } catch (error: any) {
      console.error('Error getting PDF:', error);
      message.error(error.message || 'Failed to load PDF');
    }
  };

  const getStatusTag = (share: Share) => {
    if (!share.isActive) {
      return <Tag color="red">Revoked</Tag>;
    }

    if (share.expiresAt) {
      const now = new Date();
      const expiryDate = new Date(share.expiresAt);
      if (expiryDate < now) {
        return <Tag color="orange">Expired</Tag>;
      }
    }

    return <Tag color="green">Active</Tag>;
  };

  const formatDate = (date: Date | null) => {
    if (!date) return 'Never';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredShares = shares.filter(share => {
    if (!searchText) return true;
    const searchLower = searchText.toLowerCase();
    return share.videoTitle.toLowerCase().includes(searchLower);
  });

  // Desktop columns
  const columns = [
    {
      title: 'Video',
      dataIndex: 'videoTitle',
      key: 'videoTitle',
      render: (title: string, record: Share) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: '4px' }}>{title}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            <Space size="small">
              {record.hasPassword && (
                <Tooltip title="Password protected">
                  <LockOutlined style={{ color: '#faad14' }} />
                </Tooltip>
              )}
              {getStatusTag(record)}
            </Space>
          </div>
        </div>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: Date) => formatDate(date),
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      width: 150,
      render: (date: Date | null) => (
        <Space>
          <CalendarOutlined style={{ color: '#999' }} />
          {formatDate(date)}
        </Space>
      ),
    },
    {
      title: 'Views',
      dataIndex: 'viewCount',
      key: 'viewCount',
      width: 80,
      render: (count: number) => (
        <Tag color="blue">{count}</Tag>
      ),
    },
    {
      title: 'Recipients',
      dataIndex: 'recipients',
      key: 'recipients',
      width: 120,
      render: (recipients: string[]) => (
        <Tooltip title={recipients.join(', ')}>
          <Space>
            <MailOutlined style={{ color: '#999' }} />
            <span>{recipients.length}</span>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 220,
      render: (record: Share) => (
        <Space wrap>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopyLink(record.shareUrl)}
          >
            Copy Link
          </Button>
          <Button
            type="text"
            size="small"
            icon={<MessageOutlined />}
            onClick={() => handleViewComments(record)}
          >
            Comments
          </Button>
          {record.trackingEnabled && (
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewAnalytics(record)}
            >
              Analytics
            </Button>
          )}
          {record.isActive && (
            <Button
              type="text"
              size="small"
              danger
              icon={<StopOutlined />}
              onClick={() => handleRevokeShare(record.shareToken, record.videoTitle)}
            >
              Revoke
            </Button>
          )}
        </Space>
      ),
    },
  ];

  // Mobile Card Component
  const MobileShareCard = ({ share }: { share: Share }) => (
    <Card size="small" style={{ marginBottom: '12px' }}>
      {/* Header */}
      <div style={{ marginBottom: '12px' }}>
        <h4 style={{ fontWeight: 500, marginBottom: '8px' }}>
          {share.videoTitle}
        </h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
          {getStatusTag(share)}
          {share.password && (
            <Tag icon={<LockOutlined />} color="warning">Password</Tag>
          )}
          <Tag icon={<EyeOutlined />} color="blue">{share.viewCount} views</Tag>
        </div>
      </div>

      {/* Details */}
      <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
        <div style={{ marginBottom: '4px' }}>
          Created: {formatDate(share.createdAt)}
        </div>
        {share.expiresAt && (
          <div style={{ marginBottom: '4px' }}>
            Expires: {formatDate(share.expiresAt)}
          </div>
        )}
        {share.recipients.length > 0 && (
          <div>
            {share.recipients.length} recipient(s)
          </div>
        )}
      </div>

      {/* Actions */}
      <Space wrap style={{ width: '100%' }}>
        <Button
          size="small"
          icon={<CopyOutlined />}
          onClick={() => handleCopyLink(share.shareUrl)}
          style={{ flex: 1 }}
        >
          Copy Link
        </Button>
        <Button
          size="small"
          icon={<MessageOutlined />}
          onClick={() => handleViewComments(share)}
          style={{ flex: 1 }}
        >
          Comments
        </Button>
        {share.trackingEnabled && (
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewAnalytics(share)}
            style={{ flex: 1 }}
          >
            Analytics
          </Button>
        )}
        {share.isActive && (
          <Button
            size="small"
            danger
            icon={<StopOutlined />}
            onClick={() => handleRevokeShare(share.shareToken, share.videoTitle)}
            style={{ flex: 1 }}
          >
            Revoke
          </Button>
        )}
      </Space>
    </Card>
  );

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: isMobile ? '12px' : '24px' }}>
      <Card
        title={
          <Space>
            <ShareAltOutlined />
            <span>Shared Reports</span>
          </Space>
        }
        extra={
          !isMobile && (
            <Search
              placeholder="Search by video title..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
              prefix={<SearchOutlined />}
              allowClear
            />
          )
        }
      >
        {/* Mobile Search */}
        {isMobile && (
          <div style={{ marginBottom: '16px' }}>
            <Search
              placeholder="Search by video title..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              allowClear
            />
          </div>
        )}

        {/* Table/Cards */}
        {isMobile ? (
          <div>
            {filteredShares.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#999' }}>
                {searchText ? 'No shares found' : 'No shares created yet'}
              </div>
            ) : (
              filteredShares.map((share) => (
                <MobileShareCard key={share.shareToken} share={share} />
              ))
            )}
          </div>
        ) : (
          <Table
            dataSource={filteredShares}
            columns={columns}
            loading={isLoading}
            rowKey="shareToken"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} shares`,
            }}
            locale={{
              emptyText: searchText
                ? 'No shares found matching your search'
                : 'No shares created yet. Create a share link from the Videos page.',
            }}
          />
        )}
      </Card>

      {/* Analytics Drawer */}
      {selectedShare && (
        <ShareAnalyticsDrawer
          visible={analyticsVisible}
          onClose={() => {
            setAnalyticsVisible(false);
            setSelectedShare(null);
          }}
          shareToken={selectedShare.shareToken}
          videoTitle={selectedShare.videoTitle}
        />
      )}

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        visible={pdfViewerState.visible}
        onClose={() => setPdfViewerState({ visible: false, pdfUrl: '', documentId: '', documentTitle: '' })}
        pdfUrl={pdfViewerState.pdfUrl}
        documentId={pdfViewerState.documentId}
        documentTitle={pdfViewerState.documentTitle}
      />
    </div>
  );
};

export default Shares;
